import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Button,
  Input,
  Table,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import api from "../../api";
import StatsModal from "../Common/StatsModal";

const ViewTradeAccordion = ({ trade, onTradeCompleted, showPopupMessage }) => {
  // Always call hooks unconditionally
  const [open, setOpen] = useState("1");
  const [pokemonSearch, setPokemonSearch] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  // For confirmation
  const [confirmText, setConfirmText] = useState("");
  // Modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const confirmInputRef = useRef(null);
  const completingTradeRef = useRef(false);
  const deletingTradeRef = useRef(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);

  // Pokémon selections (if needed)
  const [selectedOfferPokemon, setSelectedOfferPokemon] = useState([]);
  const [selectedReceivePokemon, setSelectedReceivePokemon] = useState([]);

  // Separate states for item amounts for offered and received items
  const [offerItemAmounts] = useState({});
  const [receiveItemAmounts] = useState({});

  // Item selections (if needed)
  const [selectedOfferItems, setSelectedOfferItems] = useState([]);
  const [selectedReceiveItems, setSelectedReceiveItems] = useState([]);

  // Toggle confirmation modal
  const toggleConfirmModal = () => setConfirmModalOpen(!confirmModalOpen);
  
  // Toggle delete modal
  const toggleDeleteModal = () => setDeleteModalOpen(!deleteModalOpen);

  const toggleStatsModal = () => setStatsModalOpen((prev) => !prev);

  const handleOpenStats = async (pokemon) => {
    if (!pokemon?.id) return;
    setStatsModalOpen(true);
    setStatsLoading(true);
    setStatsData(null);
    try {
      const res = await api.get(`/v1/pokemon/${pokemon.id}`, {
        withCredentials: true,
      });
      setStatsData(res.data);
    } catch (err) {
      console.error(err);
      setStatsData({ error: "Failed to load Pokémon stats." });
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (!confirmModalOpen) return;
    const t = setTimeout(() => {
      if (confirmInputRef.current) {
        try {
          confirmInputRef.current.focus();
        } catch (e) {
          // ignore
        }
      }
    }, 150);
    return () => clearTimeout(t);
  }, [confirmModalOpen]);

  if (!trade || !trade.trade_details) {
    return <div>No trade details available.</div>;
  }

  // Get current user from localStorage. Adjust as needed.
  const currentUser = (localStorage.getItem("authUser") || "").toLowerCase();
  // Determine roles (case-insensitive comparison)
  const isReceiver =
    trade.recipient_username &&
    trade.recipient_username.toLowerCase() === currentUser;

  // Helper: Filter Pokémon list based on search input.
  const filterPokemon = (pokemonList) =>
    pokemonList.filter((poke) =>
      (poke.pokemon_name || "")
        .toLowerCase()
        .includes(pokemonSearch.toLowerCase()),
    );

  // Helper: Filter Items list based on search input.
  const filterItems = (itemsList) =>
    itemsList.filter((item) =>
      (item.item_name || "").toLowerCase().includes(itemSearch.toLowerCase()),
    );

  // Render Pokémon table (for offered/received)
  const renderPokemonTable = (data, selected, setSelected) => {
    const filteredData = filterPokemon(data);
    return (
      <>
        <Row className="mb-2 align-items-center">
          <Col xs="8">
            <Input
              type="text"
              placeholder="Search Pokémon..."
              value={pokemonSearch}
              onChange={(e) => setPokemonSearch(e.target.value)}
            />
          </Col>
        </Row>
        <Table responsive striped>
          <thead>
            <tr>
              <th>Pokémon</th>
              <th>Gender</th>
              <th>Level</th>
              <th>Stats</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((poke) => {
              const gender = poke.pokemon_gender || "";
              const level = poke.pokemon_totalexp
                ? Math.floor(Math.pow(poke.pokemon_totalexp, 1 / 3))
                : "N/A";
              return (
                <tr key={poke.id}>
                  <td>
                    <img
                      src={`/sprites/${poke.pokemon_name}.png`}
                      alt={poke.pokemon_name}
                      style={{ width: "60px" }}
                    />
                  </td>
                  <td>{gender}</td>
                  <td>
                    {typeof level === "number" ? level.toLocaleString() : level}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      color="info"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenStats(poke);
                      }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </>
    );
  };

  // Render Items table (for offered/received items).
  // isOffer = true uses offerItemAmounts; false uses receiveItemAmounts.
  const renderItemsTable = (data, selected, setSelected, isOffer = true) => {
    const filteredData = filterItems(data);
    void selected;
    void setSelected;
    void isOffer;
    return (
      <>
        <Row className="mb-2">
          <Col xs="8">
            <Input
              type="text"
              placeholder="Search Items..."
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
            />
          </Col>
        </Row>
        <Table responsive striped>
          <thead>
            <tr>
              <th>Item</th>
              <th>Name</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item) => (
              <tr key={item.item}>
                <td>
                  <img
                    src={item.item_image}
                    alt={item.item_name}
                    style={{ width: "32px" }}
                  />
                </td>
                <td>{item.item_name}</td>
                <td>{item.amount}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    );
  };

  // Handler for completing the trade (only allowed for receiver)
  const handleCompleteTrade = async () => {
    if (completingTradeRef.current) return;
    completingTradeRef.current = true;

    // Close the modal
    setConfirmModalOpen(false);

    const payload = {
      tradeid: trade.tradeid,
      trade_details: {
        pokemon_offer:
          selectedOfferPokemon.length > 0
            ? selectedOfferPokemon.map((p) => ({ id: p.id }))
            : [],
        pokemon_receive:
          selectedReceivePokemon.length > 0
            ? selectedReceivePokemon.map((p) => ({ id: p.id }))
            : [],
        items_offer:
          selectedOfferItems.length > 0
            ? selectedOfferItems.map((item) => ({
                item: item.item,
                amount: Number(offerItemAmounts[item.item] || 0),
              }))
            : [],
        items_receive:
          selectedReceiveItems.length > 0
            ? selectedReceiveItems.map((item) => ({
                item: item.item,
                amount: Number(receiveItemAmounts[item.item] || 0),
              }))
            : [],
      },
    };

    try {
      const res = await api.post(
        `/v1/complete-trade/${trade.tradeid}`,
        payload,
        { withCredentials: true },
      );
      if (res.data && res.data.status === "success") {
        showPopupMessage("Trade completed successfully!", "", "#4a6cde");
        setOpen("");
        if (typeof onTradeCompleted === "function") {
          onTradeCompleted(trade.tradeid);
        }
      } else {
        showPopupMessage("Failed to complete trade.", "", "#f44336");
      }
    } catch (err) {
      console.error("Error completing trade", err);
      showPopupMessage("Error completing trade.", "", "#f44336");
    } finally {
      completingTradeRef.current = false;
    }
  };

  // Handler for deleting a trade (for sender)
  const handleDeleteTrade = async () => {
    if (deletingTradeRef.current) return;
    deletingTradeRef.current = true;

    // Close the modal
    setDeleteModalOpen(false);
    try {
      const res = await api.delete(`/v1/trade/${trade.tradeid}`, {
        withCredentials: true,
      });
      if (res.data && res.data.status === "success") {
        showPopupMessage("Trade deleted successfully!", "", "#4a6cde");
        setOpen("");
        if (typeof onTradeCompleted === "function") {
          onTradeCompleted(trade.tradeid);
        }
      } else {
        showPopupMessage("Failed to delete trade.", "", "#f44336");
      }
    } catch (err) {
      console.error("Error deleting trade", err);
      showPopupMessage("Error deleting trade.", "", "#f44336");
    } finally {
      deletingTradeRef.current = false;
    }
  };

  return (
    <div className="trade-details">
      {trade && (
        <div>
          {/* Display the trade number at the top */}
          <h5 className="trade-details__title">
            Trade #{trade.tradeid.toLocaleString()}
          </h5>
          <Accordion
            open={open}
            toggle={(id) => setOpen(open === id ? "" : id)}
            className="trade-details-accordion"
          >
            {/* Pokémon Tab */}
            <AccordionItem>
              <AccordionHeader targetId="1">Pokémon</AccordionHeader>
              <AccordionBody accordionId="1">
                {(!trade.trade_details.pokemon_offer ||
                  trade.trade_details.pokemon_offer.length === 0) &&
                (!trade.trade_details.pokemon_receive ||
                  trade.trade_details.pokemon_receive.length === 0) ? (
                  <p className="trade-empty">No Pokémon were part of this trade.</p>
                ) : (
                  <div className="trade-offer-grid">
                    {trade.trade_details.pokemon_offer &&
                    trade.trade_details.pokemon_offer.length > 0 ? (
                      <div className="trade-offer-panel">
                        <h6 className="trade-offer-title">
                          {isReceiver ? "Their Pokémon" : "Your Pokémon"}
                        </h6>
                        {renderPokemonTable(
                          trade.trade_details.pokemon_offer,
                          selectedOfferPokemon,
                          setSelectedOfferPokemon,
                        )}
                      </div>
                    ) : (
                      <div className="trade-offer-panel is-empty">
                        <p className="trade-empty">No Pokémon were offered.</p>
                      </div>
                    )}
                    {trade.trade_details.pokemon_receive &&
                    trade.trade_details.pokemon_receive.length > 0 ? (
                      <div className="trade-offer-panel">
                        <h6 className="trade-offer-title">
                          {isReceiver ? "Your Pokémon" : "Their Pokémon"}
                        </h6>
                        {renderPokemonTable(
                          trade.trade_details.pokemon_receive,
                          selectedReceivePokemon,
                          setSelectedReceivePokemon,
                        )}
                      </div>
                    ) : (
                      <div className="trade-offer-panel is-empty">
                        <p className="trade-empty">No Pokémon were selected on your side of the trade.</p>
                      </div>
                    )}
                  </div>
                )}
              </AccordionBody>
            </AccordionItem>
            {/* Items Tab */}
            <AccordionItem>
              <AccordionHeader targetId="2">Items</AccordionHeader>
              <AccordionBody accordionId="2">
                {(!trade.trade_details.items_offer ||
                  trade.trade_details.items_offer.length === 0) &&
                (!trade.trade_details.items_receive ||
                  trade.trade_details.items_receive.length === 0) ? (
                  <p className="trade-empty">No items were part of this trade.</p>
                ) : (
                  <div className="trade-offer-grid">
                    {trade.trade_details.items_offer &&
                    trade.trade_details.items_offer.length > 0 ? (
                      <div className="trade-offer-panel">
                        <h6 className="trade-offer-title">Their Items</h6>
                        {renderItemsTable(
                          trade.trade_details.items_offer,
                          selectedOfferItems,
                          setSelectedOfferItems,
                          true,
                        )}
                      </div>
                    ) : (
                      <div className="trade-offer-panel is-empty">
                        <p className="trade-empty">No items were offered.</p>
                      </div>
                    )}
                    {trade.trade_details.items_receive &&
                    trade.trade_details.items_receive.length > 0 ? (
                      <div className="trade-offer-panel">
                        <h6 className="trade-offer-title">Your Items</h6>
                        {renderItemsTable(
                          trade.trade_details.items_receive,
                          selectedReceiveItems,
                          setSelectedReceiveItems,
                          false,
                        )}
                      </div>
                    ) : (
                      <div className="trade-offer-panel is-empty">
                        <p className="trade-empty">No items were selected on your side of the trade.</p>
                      </div>
                    )}
                  </div>
                )}
              </AccordionBody>
            </AccordionItem>
            {/* Currency Tab */}
            <AccordionItem>
              <AccordionHeader targetId="3">Currency</AccordionHeader>
              <AccordionBody accordionId="3">
                {trade.money_from === 0 &&
                trade.money_to === 0 &&
                trade.coins_from === 0 &&
                trade.coins_to === 0 ? (
                  <p>No currency was part of this trade.</p>
                ) : (
                  <>
                    {/*
                      Determine “give” vs. “receive” based on whether
                      the current user is the recipient or the sender.
                    */}
                    <div className="trade-currency-grid">
                    {(() => {
                      // If I’m the receiver, I “give” money_to and “receive” money_from.
                      // If I’m the sender, I “give” money_from and “receive” money_to.
                      const giveMoney = isReceiver
                        ? trade.money_to
                        : trade.money_from;
                      const receiveMoney = isReceiver
                        ? trade.money_from
                        : trade.money_to;

                      // Same logic for coins:
                      const giveCoins = isReceiver
                        ? trade.coins_to
                        : trade.coins_from;
                      const receiveCoins = isReceiver
                        ? trade.coins_from
                        : trade.coins_to;

                      return (
                        <>
                          <Row className="mb-3">
                            <Col xs="6">
                              <div
                                style={{
                                  fontWeight: "bold",
                                  marginBottom: "4px",
                                }}
                              >
                                <img
                                  src="/items/money.png"
                                  alt="money"
                                  style={{
                                    width: "24px",
                                    verticalAlign: "middle",
                                    marginRight: "8px",
                                  }}
                                />
                                Money to Give:
                              </div>
                              <Input
                                type="number"
                                value={giveMoney}
                                readOnly
                              />
                            </Col>
                            <Col xs="6">
                              <div
                                style={{
                                  fontWeight: "bold",
                                  marginBottom: "4px",
                                }}
                              >
                                <img
                                  src="/items/money.png"
                                  alt="money"
                                  style={{
                                    width: "24px",
                                    verticalAlign: "middle",
                                    marginRight: "8px",
                                  }}
                                />
                                Money to Receive:
                              </div>
                              <Input
                                type="number"
                                value={receiveMoney}
                                readOnly
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col xs="6">
                              <div
                                style={{
                                  fontWeight: "bold",
                                  marginBottom: "4px",
                                }}
                              >
                                <img
                                  src="/items/coin.png"
                                  alt="coin"
                                  style={{
                                    width: "24px",
                                    verticalAlign: "middle",
                                    marginRight: "8px",
                                  }}
                                />
                                Coins to Give:
                              </div>
                              <Input
                                type="number"
                                value={giveCoins}
                                readOnly
                              />
                            </Col>
                            <Col xs="6">
                              <div
                                style={{
                                  fontWeight: "bold",
                                  marginBottom: "4px",
                                }}
                              >
                                <img
                                  src="/items/coin.png"
                                  alt="coin"
                                  style={{
                                    width: "24px",
                                    verticalAlign: "middle",
                                    marginRight: "8px",
                                  }}
                                />
                                Coins to Receive:
                              </div>
                              <Input
                                type="number"
                                value={receiveCoins}
                                readOnly
                              />
                            </Col>
                          </Row>
                        </>
                      );
                    })()}
                    </div>
                  </>
                )}
              </AccordionBody>
            </AccordionItem>

          </Accordion>
        </div>
      )}
      {trade && (
        <>
          <div className="trade-action-bar">
            {isReceiver ? (
              <Button
                color="success"
                className="trade-action-btn"
                onClick={toggleConfirmModal}
              >
                Complete Trade
              </Button>
            ) : (
              <Button 
                color="danger" 
                className="trade-action-btn" 
                onClick={toggleDeleteModal}
              >
                Delete Trade
              </Button>
            )}
          </div>
          
          {/* Confirmation Modal */}
          <Modal
            isOpen={confirmModalOpen}
            toggle={toggleConfirmModal}
            centered={true}
            className="trade-confirm-modal"
          >
            <ModalHeader toggle={toggleConfirmModal}>
              Confirm Trade
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to complete this trade?</p>
              <p className="text-muted">Type "yes" to confirm:</p>
              <Input
                type="text"
                placeholder="Type 'yes' to confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mb-3"
                innerRef={confirmInputRef}
              />
            </ModalBody>
            <ModalFooter>
              <Button 
                color="secondary" 
                onClick={toggleConfirmModal}
              >
                Cancel
              </Button>
              <Button
                color="success"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onPointerUp={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCompleteTrade();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCompleteTrade();
                }}
                disabled={confirmText.toLowerCase() !== "yes"}
              >
                Complete Trade
              </Button>
            </ModalFooter>
          </Modal>
          
          {/* Delete Modal */}
          <Modal
            isOpen={deleteModalOpen}
            toggle={toggleDeleteModal}
            centered={true}
            className="trade-delete-modal"
          >
            <ModalHeader toggle={toggleDeleteModal}>
              Delete Trade
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to delete this trade?</p>
              <p>This action cannot be undone.</p>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="secondary" 
                onClick={toggleDeleteModal}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onPointerUp={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteTrade();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteTrade();
                }}
              >
                Delete Trade
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}

      <StatsModal
        isOpen={statsModalOpen}
        toggle={toggleStatsModal}
        loading={statsLoading}
        data={statsData}
      />
    </div>
  );
};

export default ViewTradeAccordion;
